import { Droplets, Tractor } from "lucide-react";
import CustomProductName from "@/components/desktop/products/product_name/CustomProductName";
import { formatNumber, truncateToDecimals } from "@/utils/numberFormat";
import styles from "./OAProductsTable.module.scss";

const resolveDoseHa = (detail, wetness) => {
	if (detail?.dose_ha != null && detail?.dose_ha !== "") {
		return Number(detail.dose_ha);
	}

	const dose100L = Number(detail?.dose_100l ?? detail?.dose_100L ?? 0);
	const referenceWetness = Number(detail?.wetness ?? wetness ?? 0);

	if (!dose100L || !referenceWetness) {
		return null;
	}

	return (dose100L * referenceWetness) / 100 / 1000;
};

const groupDetails = (details = []) => {
	const groupedMap = new Map();

	details.forEach((detail) => {
		const groupKey = Number(detail?.is_copy || 0) === 1 ? "copy" : "base";
		if (!groupedMap.has(groupKey)) {
			groupedMap.set(groupKey, []);
		}
		groupedMap.get(groupKey).push(detail);
	});

	return Array.from(groupedMap.values());
};

const resolveBlockHeader = (group, fallbackMachinedLiters) => {
	const firstDetail = group[0] || {};
	const quantityMachinery = Number(firstDetail?.quantity_machinery || 0);
	const machinedLiters = Number(firstDetail?.machined || fallbackMachinedLiters || 0);

	if (!quantityMachinery && !machinedLiters) {
		return "";
	}

	return `${formatNumber(quantityMachinery)} maquinadas con ${formatNumber(machinedLiters)} litros`;
};

const normalizeProductOptions = (options = []) => {
	if (!Array.isArray(options)) {
		return [];
	}

	return options
		.map((option) => {
			if (typeof option === "string") {
				return option.trim();
			}
			return String(option?.product_name || "").trim();
		})
		.filter(Boolean);
};

const formatValue = (value) => {
	if (value == null || value === "") {
		return "-";
	}

	const asNumber = Number(value);
	if (Number.isNaN(asNumber)) {
		return String(value);
	}

	return formatNumber(asNumber);
};

const buildProductTableRows = (group) => {
	const tableRows = [];

	group.forEach((detail, detailIndex) => {
		const rowKey = `${detail?.detail_id || "detail"}-${detailIndex}`;
		tableRows.push({ rowKey, rowType: "main", detail });

		normalizeProductOptions(detail?.options).forEach((productName, optionIndex) => {
			tableRows.push({
				rowKey: `${rowKey}-option-${optionIndex}`,
				rowType: "option",
				productName,
			});
		});
	});

	return tableRows;
};

const OAProductMainRow = ({ detail, wetness }) => (
	<>
		<td className={styles.bodyCell}>
			<div className={styles.productCell}>
				<CustomProductName
					product={{
						code: detail?.product_code,
						value: detail?.product_id,
						label: detail?.product_name || detail?.product_text || "-",
					}}
				/>
				{detail?.inventory_codes ? <span className={styles.inventoryCodes}>{detail.inventory_codes}</span> : null}
			</div>
		</td>
		<td className={styles.bodyCell}>{detail?.objective_name || detail?.application_orders_objetives_name || "-"}</td>
		<td className={`${styles.bodyCell} ${styles.numericBodyCell}`}>{formatValue(detail?.dose_100l ?? detail?.dose_100L)}</td>
		<td className={`${styles.bodyCell} ${styles.numericBodyCell}`}>
			{(() => {
				const rawValue = detail?.quantity ?? resolveDoseHa(detail, wetness);
				if (rawValue == null || rawValue === "") return "-";
				const asNumber = Number(rawValue);
				if (Number.isNaN(asNumber)) return String(rawValue);
				return <strong>{formatNumber(truncateToDecimals(asNumber, 2))}</strong>;
			})()}
		</td>
	</>
);

const OAProductOptionRow = ({ productName }) => (
	<>
		<td className={`${styles.bodyCell} ${styles.optionProductCell}`}>
			<span className={styles.optionLine}>
				<span className={styles.optionPrefix}>o</span>
				{productName}
			</span>
		</td>
		<td className={`${styles.bodyCell} ${styles.optionMutedCell}`}>-</td>
		<td className={`${styles.bodyCell} ${styles.optionMutedCell} ${styles.numericBodyCell}`}>-</td>
		<td className={`${styles.bodyCell} ${styles.optionMutedCell} ${styles.numericBodyCell}`}>-</td>
	</>
);

const OAProductsTable = ({ details = [], wetness = 0, machinedLiters = 0, applicationOrderTypeId = 0 }) => {
	if (!Array.isArray(details) || details.length === 0) {
		return <span>-</span>;
	}

	const groupedDetails = groupDetails(details);

	return (
		<div>
			{groupedDetails.map((group, groupIndex) => {
				const sectionTitle = resolveBlockHeader(group, machinedLiters);
				const tableRows = buildProductTableRows(group);

				return (
					<div key={`group-${groupIndex}`} className={styles.section}>
						{Number(applicationOrderTypeId) !== 2 && sectionTitle && (
							<div className={styles.sectionHeader}>
								<Tractor size={14} color="#15803d" />
								<span>{sectionTitle}</span>
							</div>
						)}
						<div className={styles.tableShell}>
							<table className={styles.table}>
								<thead>
									<tr>
										<th className={styles.headerCell} style={{ width: "46%" }}>
											Producto
										</th>
										<th className={styles.headerCell} style={{ width: "24%" }}>
											Objetivo
										</th>
										<th className={`${styles.headerCell} ${styles.numericHeaderCell}`} style={{ width: "14%" }}>
											<div className={styles.sectionTitleWrap}>
												<Droplets size={12} />
												<span>Dosis/100L</span>
											</div>
										</th>
										<th className={`${styles.headerCell} ${styles.numericHeaderCell}`} style={{ width: "16%" }}>
											Cant. (L/Kg)
										</th>
									</tr>
								</thead>
								<tbody>
									{tableRows.map((tableRow) => (
										<tr
											key={`${tableRow.rowKey}-${groupIndex}`}
											className={tableRow.rowType === "option" ? styles.optionRow : styles.tableRow}
										>
											{tableRow.rowType === "main" ? (
												<OAProductMainRow detail={tableRow.detail} wetness={wetness} />
											) : (
												<OAProductOptionRow productName={tableRow.productName} />
											)}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default OAProductsTable;
